import React from 'react';
import clsx from 'clsx';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Spinner } from '@heroui/spinner';
import { APP_EVENTS, logHubxEvent } from '@/app/providers/analytics';
import { NextUI } from '@/components/NextUI';
import PdfFile from '@/components/Common/Icons/PdfFile';
import { useAppSelector } from '@/hooks/redux';
import { formatAnalyticsSource } from '@/helpers/common';
import formatSize from '@/helpers/format-size';
import { buildStructuredAnalyticsSource } from '@/lib/analytics/utils/source';
import {
  getDownloadSignupErrorMessage,
  isValidDownloadEmail,
  useDownloadEmailSignup,
} from '@/features/tool/hooks/useDownloadEmailSignup';
import StatusGlow from './EditShareModal.icons/StatusGlow';
import StatusCheck from './EditShareModal.icons/StatusCheck';
import DownloadArrow from './EditShareModal.icons/DownloadArrow';
import { OPTIONS, FileTypeIcon } from './EditShareModal.constants';
import { EditShareModalData, EditShareOptionId } from './EditShareModal.types';

const EMAIL_INPUT_ID = 'edit-share-download-email';

const DOWNLOAD_VARIANT_PARAMS = { downloadVariant: 1 };

const EditShareDownloadReadyModal = ({
  data,
  hideModal,
}: {
  data?: EditShareModalData;
  hideModal: () => void;
}) => {
  const t = useTranslations('download_page');
  const user = useAppSelector((state) => state.auth.user);
  const [isGuest] = React.useState(() => !user);
  const { submitEmail } = useDownloadEmailSignup();
  const [selectedOptionId, setSelectedOptionId] =
    React.useState<EditShareOptionId>(data?.selectedOptionId ?? 'pdf');
  const [email, setEmail] = React.useState('');
  const [emailError, setEmailError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const isMountedRef = React.useRef(true);
  const isSubmittingRef = React.useRef(false);
  const toolType = data?.toolType;
  const formattedToolType = React.useMemo(
    () => formatAnalyticsSource(toolType),
    [toolType]
  );
  const analyticsSource = React.useMemo(
    () => buildStructuredAnalyticsSource(toolType, data?.originEntryPoint),
    [data?.originEntryPoint, toolType]
  );
  const fileName = data?.fileName ?? '';
  const summary = data?.fileSummary;
  const summaryDetails = [
    typeof summary?.pageCount === 'number'
      ? t('page_count', { count: summary.pageCount })
      : null,
    typeof summary?.size === 'number' ? formatSize(summary.size) : null,
  ].filter(Boolean);
  const selectedOption =
    OPTIONS.find((option) => option.id === selectedOptionId) ?? OPTIONS[0];
  const downloadLabel =
    selectedOption.id === 'compressed'
      ? t('download_generic')
      : t('download_format', { format: selectedOption.label });
  const hasEmailError = Boolean(emailError);

  React.useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    logHubxEvent({
      eventName: APP_EVENTS.LND_DOWNLOAD_POPUP,
      source: formattedToolType,
      event_params: {
        source: analyticsSource,
        ...DOWNLOAD_VARIANT_PARAMS,
      },
    });
  }, [analyticsSource, formattedToolType]);

  const handleClose = React.useCallback(() => {
    if (isSubmittingRef.current) {
      return;
    }

    data?.onContinueEdit?.(fileName);
    hideModal();
  }, [data, fileName, hideModal]);

  const handleContinueEdit = React.useCallback(() => {
    if (isSubmittingRef.current) {
      return;
    }

    logHubxEvent({
      eventName: APP_EVENTS.BTN_CONTINUE_POPUP,
      source: formattedToolType,
      event_params: {
        source: analyticsSource,
      },
    });
    data?.onContinueEdit?.(fileName);
    hideModal();
  }, [analyticsSource, data, fileName, formattedToolType, hideModal]);

  const submit = React.useCallback(async () => {
    if (isSubmittingRef.current || !data) {
      return;
    }

    const optionId = selectedOptionId;
    const confirmDownload = () =>
      data.onConfirm?.(optionId, fileName, { isDownloadClickTracked: true });

    if (!isGuest) {
      data.onDownloadClick?.(optionId);
      hideModal();
      confirmDownload();
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();

    if (!isValidDownloadEmail(normalizedEmail)) {
      setEmailError(t('invalid_email'));
      return;
    }

    data.onDownloadClick?.(optionId);
    logHubxEvent({
      eventName: APP_EVENTS.BTN_DOWNLOAD_EMAIL,
      source: analyticsSource.toolType,
      event_params: {
        source: analyticsSource,
      },
    });

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setEmailError(null);
    setEmail(normalizedEmail);

    try {
      const result = await submitEmail(normalizedEmail, {
        extension: data.extension,
        toolType,
        originEntryPoint: data.originEntryPoint,
        onSuccess: confirmDownload,
      });

      isSubmittingRef.current = false;

      if (result === 'registered') {
        hideModal();
        confirmDownload();
      }
    } catch (signupError) {
      isSubmittingRef.current = false;

      if (isMountedRef.current) {
        setEmailError(getDownloadSignupErrorMessage(signupError));
        setIsSubmitting(false);
      }
    }
  }, [
    analyticsSource,
    data,
    email,
    fileName,
    hideModal,
    isGuest,
    selectedOptionId,
    submitEmail,
    t,
    toolType,
  ]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submit();
  };

  return (
    <NextUI.Modal
      className="mx-4 my-4 max-w-[670px] rounded-[16px] shadow-none sm:my-4"
      classNames={{
        content:
          'relative max-h-[calc(100dvh-32px)] overflow-x-hidden overflow-y-auto rounded-[16px] bg-white p-4 shadow-none md:px-12 md:py-8 md:[@media(min-height:760px)_and_(max-height:899px)]:py-6 md:[@media(min-height:680px)_and_(max-height:759px)]:py-4 md:[@media(max-height:679px)]:py-3 max-md:[@media(max-height:799px)]:py-3',
        backdrop: 'bg-[rgba(124,124,124,0.5)]',
      }}
      defaultOpen={true}
      name="EditShare"
      backdrop="opaque"
      hideCloseButton={true}
      onClose={handleClose}
    >
      <StatusGlow className="pointer-events-none absolute left-0 top-0 h-[152px] w-[670px]" />

      <form
        noValidate
        onSubmit={handleSubmit}
        className="relative flex flex-col gap-4 md:gap-8 md:[@media(min-height:760px)_and_(max-height:899px)]:gap-5 md:[@media(min-height:680px)_and_(max-height:759px)]:gap-3 md:[@media(max-height:679px)]:gap-2 max-md:[@media(max-height:799px)]:gap-2"
      >
        <div className="flex flex-col items-center gap-2 text-center md:gap-4 md:py-4 md:[@media(min-height:760px)_and_(max-height:899px)]:gap-3 md:[@media(min-height:760px)_and_(max-height:899px)]:py-0 md:[@media(min-height:680px)_and_(max-height:759px)]:gap-3 md:[@media(min-height:680px)_and_(max-height:759px)]:py-0 md:[@media(max-height:679px)]:gap-2 md:[@media(max-height:679px)]:py-0">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-[0px_24px_32px_0px_rgba(0,0,0,0.08)] md:h-16 md:w-16 md:[@media(min-height:680px)_and_(max-height:759px)]:h-12 md:[@media(min-height:680px)_and_(max-height:759px)]:w-12 md:[@media(max-height:679px)]:h-10 md:[@media(max-height:679px)]:w-10">
            <StatusCheck className="h-6 w-6 text-[#31CA93] md:h-10 md:w-10 md:[@media(min-height:680px)_and_(max-height:759px)]:h-8 md:[@media(min-height:680px)_and_(max-height:759px)]:w-8 md:[@media(max-height:679px)]:h-6 md:[@media(max-height:679px)]:w-6" />
          </div>

          <div className="flex w-full flex-col items-center gap-1 break-words">
            <span className="w-full font-barlow text-[20px] font-semibold leading-[24px] text-[#454655] md:text-[28px] md:leading-[34px] md:[@media(min-height:680px)_and_(max-height:759px)]:text-[24px] md:[@media(min-height:680px)_and_(max-height:759px)]:leading-[29px] md:[@media(max-height:679px)]:text-[20px] md:[@media(max-height:679px)]:leading-[24px]">
              {t('title')}
            </span>
            <span className="w-full font-barlow text-[15px] font-normal leading-[18px] text-[#787887] md:text-[17px] md:leading-[20px]">
              {isGuest ? t('guest_description') : t('description')}
            </span>
          </div>
        </div>

        {summary ? (
          <div className="flex w-full items-center gap-2 rounded-xl border border-solid border-[#E5E6EB] bg-[rgba(255,255,255,0.04)] px-4 py-3 md:[@media(max-height:679px)]:py-2 max-md:[@media(max-height:799px)]:py-2">
            <div className="relative h-9 w-9 shrink-0">
              <PdfFile className="absolute left-px top-0 h-9 w-[34px]" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="truncate font-barlow text-[17px] font-normal leading-[20px] text-[#454655]">
                {summary.name}
              </span>
              {summaryDetails.length > 0 ? (
                <span className="truncate font-barlow text-[15px] font-normal leading-[18px] text-[#787887]">
                  {summaryDetails.join(' • ')}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-3 md:gap-4 md:[@media(min-height:760px)_and_(max-height:899px)]:gap-3 md:[@media(min-height:680px)_and_(max-height:759px)]:gap-3 md:[@media(max-height:679px)]:gap-2">
          <span className="font-barlow text-[17px] font-semibold leading-[20px] text-[#454655]">
            {t('export_as')}
          </span>
          <div className="grid grid-cols-1 gap-1 md:grid-cols-2 md:gap-3 md:[@media(min-height:760px)_and_(max-height:899px)]:gap-2 md:[@media(min-height:680px)_and_(max-height:759px)]:gap-2 md:[@media(max-height:679px)]:gap-2">
            {OPTIONS.map((option) => {
              const isSelected = option.id === selectedOptionId;

              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setSelectedOptionId(option.id)}
                  className={clsx(
                    'flex items-center gap-2 rounded-xl px-4 py-2 text-left transition-colors md:py-3 md:[@media(min-height:680px)_and_(max-height:759px)]:py-2 md:[@media(max-height:679px)]:py-1.5 max-md:[@media(max-height:799px)]:py-1.5',
                    isSelected
                      ? 'border border-solid border-[#6FA9FF] bg-[#F2F8FD]'
                      : 'bg-[rgba(0,0,0,0.04)]'
                  )}
                >
                  <span className="flex flex-1 items-center gap-2">
                    <span
                      className={clsx(
                        'flex h-4 w-4 items-center justify-center rounded-full border',
                        isSelected ? 'border-[#4B7EFF]' : 'border-[#CECFD4]'
                      )}
                    >
                      {isSelected ? (
                        <span className="h-2 w-2 rounded-full bg-[#4B7EFF]" />
                      ) : null}
                    </span>
                    <span className="font-barlow text-[16px] font-medium leading-[24px] text-[#454655] md:text-[20px]">
                      {option.label}
                    </span>
                  </span>

                  <FileTypeIcon
                    color={option.color}
                    glyphTop={option.glyphTop}
                    Glyph={option.Glyph}
                    badge={option.badge}
                  />
                </button>
              );
            })}
          </div>
        </div>

        {isGuest ? (
          <div className="flex w-full flex-col items-start gap-2">
            <label
              htmlFor={EMAIL_INPUT_ID}
              className="font-barlow text-[17px] font-semibold leading-[20px] text-[#454655]"
            >
              {t('email_label')}
            </label>
            <div className="relative h-12 w-full md:[@media(max-height:679px)]:h-10 max-md:[@media(max-height:799px)]:h-10">
              <input
                id={EMAIL_INPUT_ID}
                type="text"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);

                  if (emailError) {
                    setEmailError(null);
                  }
                }}
                placeholder={t('email_placeholder')}
                disabled={isSubmitting}
                aria-invalid={hasEmailError}
                aria-describedby={
                  hasEmailError ? `${EMAIL_INPUT_ID}-error` : undefined
                }
                className="absolute inset-0 h-full w-full rounded-[5px] bg-transparent px-4 font-barlow text-[15px] font-normal leading-[normal] text-[#454655] outline-none placeholder:text-[#787887] disabled:cursor-not-allowed"
              />
              <div
                aria-hidden
                className={clsx(
                  'pointer-events-none absolute inset-0 rounded-[5px] border-solid',
                  hasEmailError
                    ? 'border-2 border-[#F43F5E]'
                    : 'border border-[#CECFD4]'
                )}
              />
            </div>
            {hasEmailError ? (
              <span
                id={`${EMAIL_INPUT_ID}-error`}
                role="alert"
                className="font-barlow text-[13px] font-normal leading-[16px] text-[#F43F5E]"
              >
                {emailError}
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-2 md:flex-row">
            <button
              type="button"
              onClick={handleContinueEdit}
              disabled={isSubmitting}
              className="flex h-12 w-full items-center justify-center rounded-[5px] border border-solid border-[#787887] md:[@media(max-height:679px)]:h-10 max-md:[@media(max-height:799px)]:h-10 bg-white p-2 font-barlow text-[17px] font-medium leading-[20px] text-[#454655] outline-none transition-colors enabled:hover:bg-[#F7F7F9] disabled:cursor-not-allowed disabled:opacity-40 md:flex-1"
            >
              {t('continue_editing')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              aria-busy={isSubmitting}
              className="flex h-12 w-full items-center justify-center gap-1 rounded-[5px] bg-[#4B7EFF] md:[@media(max-height:679px)]:h-10 max-md:[@media(max-height:799px)]:h-10 p-2 font-barlow text-[17px] font-medium leading-[20px] text-white outline-none transition-colors enabled:hover:bg-primary-dark-blue disabled:cursor-wait md:flex-1"
            >
              {isSubmitting ? (
                <Spinner size="sm" color="white" />
              ) : (
                <>
                  <DownloadArrow className="h-6 w-6" />
                  {downloadLabel}
                </>
              )}
            </button>
          </div>
          <p className="text-center font-barlow text-[12px] font-normal leading-[20px] text-[#787887]">
            {t.rich('terms', {
              terms: (chunks) => (
                <Link
                  href="/terms"
                  target="_blank"
                  className="text-[#4B7EFF] underline"
                >
                  {chunks}
                </Link>
              ),
              privacy: (chunks) => (
                <Link
                  href="/privacy"
                  target="_blank"
                  className="text-[#4B7EFF] underline"
                >
                  {chunks}
                </Link>
              ),
            })}
          </p>
        </div>
      </form>
    </NextUI.Modal>
  );
};

export default EditShareDownloadReadyModal;
